import React, {FC} from 'react';
import styled from 'styled-components';

import {TeammateAvailabilityStatusesEnum} from '../../../libs/shared/core/src/models/api/teammateModel';
import {DropdownBoxCoordinator} from '../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {DropdownItem} from '../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemLeftIconBase} from '../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemLeftIconBase';
import {SimpleDropdown} from '../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {computeTeammateAvailabilityValues} from './teammateAvailabilityHelpers';

/*
 * Props.
 */

interface TeammateAvailabilityDropdownProps {
  selectedStatus: TeammateAvailabilityStatusesEnum;
  onClick: (status: TeammateAvailabilityStatusesEnum) => void;
}

/*
 * Styles.
 */

const ValueDiv = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CircleBackgroundIcon = styled.div<{$color: string}>`
  border-radius: 50%;
  height: 8px;
  width: 8px;
  background-color: ${(p) => p.$color};
  color: ${(p) => p.theme.greys.white};
  // Makes it a 16x16 element.
  margin: 4px;
`;

/*
 * Component.
 */

export const TeammateAvailabilityDropdown: FC<TeammateAvailabilityDropdownProps> = (props) => {
  const {selectedStatus, onClick} = props;
  const theme = useUserTheme();

  const renderDropdownValue = (status: TeammateAvailabilityStatusesEnum) => {
    const {color, text} = computeTeammateAvailabilityValues(status);
    return (
      <ValueDiv>
        <CircleBackgroundIcon $color={theme.palette[color].shade40} />
        {text}
      </ValueDiv>
    );
  };
  const renderDropdownItem = (status: TeammateAvailabilityStatusesEnum) => {
    const {color, text, tooltip} = computeTeammateAvailabilityValues(status);
    return (
      <DropdownItem
        key={status}
        shouldReserveRightIcon
        isChecked={status === selectedStatus}
        onSelect={() => onClick(status)}
      >
        <DropdownItemLeftIconBase>
          <CircleBackgroundIcon $color={theme.palette[color].shade40} />
        </DropdownItemLeftIconBase>
        <DropdownItemContent
          isChecked={status === selectedStatus}
          renderTooltip={tooltip ? () => tooltip : undefined}
        >
          {text}
        </DropdownItemContent>
      </DropdownItem>
    );
  };

  return (
    <DropdownBoxCoordinator
      value={renderDropdownValue(selectedStatus)}
      renderDropdown={(modalProps) => (
        <SimpleDropdown {...modalProps}>
          {renderDropdownItem(TeammateAvailabilityStatusesEnum.AVAILABLE)}
          {renderDropdownItem(TeammateAvailabilityStatusesEnum.BUSY)}
          {renderDropdownItem(TeammateAvailabilityStatusesEnum.OUT_OF_OFFICE)}
        </SimpleDropdown>
      )}
    />
  );
};
